import { useEffect, useState } from 'react'
import type { IntakeDomain } from '../orders/api/intakeApi'
import { useSolutions } from '../metadata/hooks/useMetadata'
import { SolutionCatalogEditor } from './components/SolutionCatalogEditor'
import { ProductAttributeSchemaPanel } from '../products/components/ProductAttributeSchemaPanel'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs'

/** A tab in the Schema Builder: the three intake domains, plus Products (attribute schemas). */
type SchemaTab = IntakeDomain | 'PRODUCTS'

const DOMAINS: { value: SchemaTab; label: string }[] = [
  { value: 'BOOKING', label: 'Booking' },
  { value: 'CUSTOM_ORDER', label: 'Custom Order' },
  { value: 'PACKAGE', label: 'Package' },
  { value: 'PRODUCTS', label: 'Products' },
]

/**
 * TCS-admin page (route `/intake-catalog`) — the single Schema Builder surface for authoring:
 *   - the SOLUTION intake-field TEMPLATE per domain (Booking / Custom Order / Package): the
 *     default CustomFieldDefinition[] merchants without their own configuration use verbatim,
 *     via fieldConfigApi (field-config/solution/:domain).
 *   - PRODUCT attribute schemas (Products tab): the dynamic per-product attributes (size,
 *     colour, …) merchants fill in when creating products, via productsApi.platform
 *     (attribute-schemas), scoped by the same Solution selector.
 * Both surfaces are solution-scoped and TCS-admin-only, so they share one page instead of
 * living as separate islands. The route is already gated by Permission.PRODUCT_SCHEMA_MANAGE
 * in App.tsx.
 */
export function SolutionCatalogPage() {
  const { data: solutions = [], isLoading } = useSolutions()
  const [solutionId, setSolutionId] = useState('')
  const [domain, setDomain] = useState<SchemaTab>('BOOKING')

  // Default to the first active solution once loaded.
  useEffect(() => {
    if (!solutionId && solutions.length) {
      const active = solutions.find((s) => s.isActive) ?? solutions[0]
      setSolutionId(active.slug)
    }
  }, [solutions, solutionId])

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Schema Builder</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Author the template intake fields (Booking, Custom Order, Package) and the product
          attribute schemas for each solution. Merchants without their own configuration use the
          template verbatim; customizing a merchant copies it as a starting point — later template
          edits do not affect merchants that already customized.
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border p-6 space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <label htmlFor="solution-select" className="text-xs font-medium text-foreground">Solution</label>
            <select
              id="solution-select"
              value={solutionId}
              onChange={(e) => setSolutionId(e.target.value)}
              disabled={isLoading || solutions.length === 0}
              className="flex h-9 min-w-56 rounded-md border border-input bg-transparent px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
            >
              {isLoading && <option value="">Loading…</option>}
              {!isLoading && solutions.length === 0 && <option value="">No solutions</option>}
              {solutions.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}{s.isActive ? '' : ' (inactive)'}</option>
              ))}
            </select>
          </div>
        </div>

        {solutionId ? (
          <Tabs value={domain} onValueChange={(v) => setDomain(v as SchemaTab)}>
            <TabsList className="mb-4">
              {DOMAINS.map((d) => (
                <TabsTrigger key={d.value} value={d.value}>{d.label}</TabsTrigger>
              ))}
            </TabsList>
            {DOMAINS.map((d) => (
              <TabsContent key={d.value} value={d.value}>
                {d.value === 'PRODUCTS' ? (
                  <ProductAttributeSchemaPanel solutionId={solutionId} />
                ) : (
                  <SolutionCatalogEditor solutionId={solutionId} domain={d.value} />
                )}
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          !isLoading && (
            <p className="text-sm text-muted-foreground">Select a solution to edit its schemas.</p>
          )
        )}
      </div>
    </div>
  )
}
